/**
 * Presentation logic for run state (prompt.md §33, §65, §21, §72).
 *
 * WHY this is not inside the component: mapping a `RunState` to a label, a
 * colour role and a spinner is a product rule — "paused because a limit was hit"
 * must read as recoverable, never as a failure. Keeping it as data means the web
 * app, the Tauri shell and (mirrored) Android all describe a run identically,
 * and it can be tested without a DOM.
 */

import type { RunPauseReason, RunState } from "@dayvise/domain";

/** Colour roles a state badge may use; these are the design-token names. */
export type StatusTone = "neutral" | "accent" | "success" | "warning" | "danger";

export interface RunStateDescriptor {
  /** Short label for a badge. */
  readonly label: string;
  /** One-line explanation shown next to the badge or in a tooltip. */
  readonly detail: string;
  readonly tone: StatusTone;
  /** True while the run is actively consuming compute or waiting on the platform. */
  readonly active: boolean;
  readonly spinning: boolean;
  /** True when the run can be resumed rather than restarted. */
  readonly resumable: boolean;
}

const DESCRIPTORS: Readonly<Record<RunState, RunStateDescriptor>> = {
  queued: { label: "Queued", detail: "Waiting for a runtime slot", tone: "neutral", active: true, spinning: false, resumable: false },
  starting: { label: "Starting", detail: "An ephemeral runtime is being prepared", tone: "accent", active: true, spinning: true, resumable: false },
  running: { label: "Running", detail: "The agent is working", tone: "accent", active: true, spinning: true, resumable: false },
  waiting_tool: { label: "Waiting on tool", detail: "A tool call is in flight", tone: "neutral", active: true, spinning: true, resumable: false },
  waiting_approval: { label: "Needs approval", detail: "The run is paused until someone approves an action", tone: "warning", active: true, spinning: false, resumable: true },
  waiting_external: { label: "Waiting", detail: "Waiting on an external service", tone: "neutral", active: true, spinning: true, resumable: true },
  paused_limit: { label: "Paused", detail: "Paused at a checkpoint; work is preserved", tone: "warning", active: false, spinning: false, resumable: true },
  paused_user: { label: "Paused", detail: "Paused by you; work is preserved", tone: "neutral", active: false, spinning: false, resumable: true },
  completed: { label: "Completed", detail: "The run finished", tone: "success", active: false, spinning: false, resumable: false },
  failed_retryable: { label: "Retrying", detail: "A transient failure occurred; the run can be retried", tone: "danger", active: false, spinning: false, resumable: true },
  failed_terminal: { label: "Failed", detail: "The run stopped and cannot resume without a new attempt", tone: "danger", active: false, spinning: false, resumable: false },
  cancelled: { label: "Cancelled", detail: "The run was cancelled", tone: "neutral", active: false, spinning: false, resumable: false },
};

/**
 * Reasons a run pauses. Every one of these is a *pause*, not a termination:
 * the run holds a checkpoint and resumes when the condition clears (prompt.md
 * §21, §33).
 */
export const RUN_PAUSE_REASON_LABELS: Readonly<Record<RunPauseReason, string>> = {
  cpu_budget_exhausted: "CPU budget exhausted",
  ai_budget_exhausted: "AI budget exhausted",
  plan_quota_reached: "Plan quota reached",
  container_time_limit: "Container time limit reached",
  storage_quota_exceeded: "Storage quota exceeded",
  provider_unavailable: "Provider unavailable",
  user_requested: "Paused by user",
  awaiting_approval: "Awaiting approval",
  awaiting_external: "Awaiting an external service",
};

export function runStateDescriptor(state: RunState): RunStateDescriptor {
  return DESCRIPTORS[state];
}

/**
 * Describe a run, refining the wording with its pause reason.
 *
 * A `paused_limit` run is deliberately labelled with the *cause* so the user
 * knows whether to top up a budget or wait for a provider.
 */
export function describeRunState(state: RunState, pauseReason?: RunPauseReason | null): RunStateDescriptor {
  const base = runStateDescriptor(state);
  if (!pauseReason) return base;
  const reason = RUN_PAUSE_REASON_LABELS[pauseReason];
  if (state === "paused_limit" || state === "paused_user") {
    return { ...base, label: reason, detail: `${reason} — the run holds a checkpoint and can resume` };
  }
  return { ...base, detail: `${base.detail} (${reason})` };
}

/** Custom property for a tone, e.g. `var(--dayvise-color-warning)`. */
export function toneColor(tone: StatusTone): string {
  switch (tone) {
    case "accent":
      return "var(--dayvise-color-accent)";
    case "success":
      return "var(--dayvise-color-success)";
    case "warning":
      return "var(--dayvise-color-warning)";
    case "danger":
      return "var(--dayvise-color-danger)";
    default:
      return "var(--dayvise-color-text-muted)";
  }
}

/** Custom property for the background of a tone chip. */
export function toneSurface(tone: StatusTone): string {
  return tone === "neutral" ? "var(--dayvise-color-surface)" : `color-mix(in srgb, ${toneColor(tone)} 14%, transparent)`;
}

/** Which control actions make sense for a run right now. */
export interface RunControlAvailability {
  readonly canSteer: boolean;
  readonly canPause: boolean;
  readonly canResume: boolean;
  readonly canCancel: boolean;
  /** Cancel is only meaningful while the platform still holds the run open. */
  readonly isLive: boolean;
}

/**
 * Derive the available controls from the state machine rather than guessing in
 * the component: steering a completed run and resuming a running one are both
 * rejected by the API, so the UI must not offer them.
 */
export function runControlAvailability(state: RunState): RunControlAvailability {
  const isLive = DESCRIPTORS[state].active;
  return {
    isLive,
    canSteer: isLive,
    canPause: state === "running" || state === "waiting_tool" || state === "waiting_external",
    canResume: state === "paused_limit" || state === "paused_user" || state === "failed_retryable",
    canCancel: isLive,
  };
}
